import { between, grow, key, pick, point, span, typed } from './engine/stage';
import { CONTACT_EMERGENCY, CONTACT_HOME, COMPLETION_POP, DASH, FRAME, HERO, NAV_STICKY, TAB } from './layout';
import { CHAPTER_KEYS } from './chapters';
import { PROJ, PROJ_DETAIL, REMINDER, TASKS } from './scenes/DashboardDialogs';
import { DEADLINE, MESSAGE_TOAST } from './scenes/DashboardScene';
import { segmentRects } from './scenes/kit';
import { ADDRESS_DLG, PASSWORD_DLG, PHONE_DLG, PHOTO_DLG, suggestionRect } from './scenes/profile/ProfileHero';
import { AGENDA, CAL } from './scenes/profile/CalendarPanes';
import { FILES, QUIZ_DLG, SOP_DLG, TRAIN_DLG } from './scenes/profile/FilesPanes';
import { MYPROJ, VISITS } from './scenes/profile/ProjectsPanes';
import { LEAVE, LEAVE_LIST } from './scenes/profile/LeavePanes';
import { EXPENSE, EXPENSE_EDIT, EXPENSE_TRACK } from './scenes/profile/ExpensePanes';
import { ANNOUNCEMENTS, BUG, MESSAGES } from './scenes/profile/ContactPanes';
import { CV } from './scenes/profile/CvPane';

/*
 * The guide, beat by beat. A beat is one caption (one narration line and one
 * written step of its chapter):
 * - scene ('title' | 'dashboard' | 'profile') + state(p): what the stage shows, p = 0 → 1
 * - cursor: keyframes { p, x, y, click } (positions carry over between beats)
 * - spot(p): the rectangle highlighted, or null
 * - toast(p): a notification on the stage
 * Durations follow the caption length so narration and reading both fit.
 */

const H0 = HERO(72);
const union = (a, b) => {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
};
const at = (p, rect, click = false) => key(p, rect, { click });

// Profile tab geometry (nav stuck under the top bar)
const T = {
  takvim: TAB('takvim'),
  ajandam: TAB('ajandam'),
  dosyalar: TAB('dosyalar'),
  projelerim: TAB('projelerim'),
  izinTalep: TAB('izin-talep'),
  izinTaleplerim: TAB('izin-taleplerim'),
  masrafTalep: TAB('masraf-talep'),
  masrafTakip: TAB('masraf-takip'),
  mesajlar: TAB('mesajlar'),
  bildirimler: TAB('bildirimler'),
  hata: TAB('sistem-hata-bildirimleri'),
  cvTr: TAB('cv-tr'),
};
const subRow = (tab) => {
  const rects = Object.values(tab.tabs);
  return grow(rects.reduce((acc, rect) => union(acc, rect)), 5);
};

const BEATS = (t) => {
  const C = CAL(T.takvim.top);
  const A = AGENDA(T.ajandam.top);
  const F = FILES(T.dosyalar.top);
  const M = MYPROJ(T.projelerim.top);
  const L = LEAVE(T.izinTalep.top);
  const LS = LEAVE(T.izinTalep.top - 150);
  const LL = LEAVE_LIST(T.izinTaleplerim.top);
  const E = EXPENSE(T.masrafTalep.top);
  const ET = EXPENSE_TRACK(T.masrafTakip.top);
  const MS = MESSAGES(T.mesajlar.top);
  const AN = ANNOUNCEMENTS(T.bildirimler.top);
  const B = BUG(T.hata.top);
  const CVR = CV(T.cvTr.top);
  const P0 = COMPLETION_POP(72);
  const taskFilter = segmentRects(TASKS.filter, TASKS.filterWidths);
  const sopFilter = segmentRects(F.filter, F.filterWidths);
  const leaveFilter = segmentRects(LL.filter, LL.filterWidths);
  const expenseFilter = segmentRects(ET.filter, ET.filterWidths);

  const heroDone = { photo: true, emergency: '0533 765 43 21', home: t('userGuide.stage.homeAddress') };
  const fresh = { photo: false, emergency: null, home: null };
  const dash = (extra = {}) => (p) => ({ unseen: 0, ...(typeof extra === 'function' ? extra(p) : extra) });
  const prof = (tab, extra = {}) => (p) => {
    const value = typeof extra === 'function' ? extra(p) : extra;
    return { scroll: 1, tab, hero: heroDone, ...value };
  };

  return [
    // ── Başlarken ──
    { id: 'intro', chapter: 'intro', scene: 'title', state: () => ({ variant: 'intro' }), cursor: null, min: 12000 },
    { id: 'howTo', chapter: 'intro', scene: 'title', state: () => ({ variant: 'howto' }), cursor: null, min: 15000 },

    {
      id: 'shellSidebar', chapter: 'shell', scene: 'dashboard',
      state: dash((p) => ({ frame: { homeOpen: p > 0.55, hover: p < 0.45 && p > 0.15 ? 'home' : p > 0.7 ? 'todo' : null } })),
      cursor: [point(0, 700, 420), at(0.25, FRAME.navHome), at(0.5, FRAME.navHomeChevron, true), at(0.75, FRAME.navTodo)],
      spot: (p) => (p < 0.5 ? grow({ x: 24, y: 84, w: 196, h: 94 }, 5) : grow({ x: 24, y: 84, w: 196, h: 138 }, 5)),
    },
    {
      id: 'shellCollapse', chapter: 'shell', scene: 'dashboard', state: dash(),
      cursor: [at(0.15, FRAME.collapse), at(0.55, FRAME.hall), at(0.8, FRAME.signOut)],
      spot: (p) => (p < 0.4 ? grow(FRAME.collapse, 8) : grow(FRAME.footer, 6)),
    },
    {
      id: 'shellSearch', chapter: 'shell', scene: 'dashboard',
      state: dash((p) => ({ frame: { searchOpen: p > 0.2, searchText: typed('Prof', p, 0.35, 0.5) } })),
      cursor: [at(0.12, FRAME.search), at(0.18, FRAME.search, true), point(0.7, 520, 84)],
      spot: (p) => (p < 0.35 ? grow(FRAME.search, 6) : grow(union(FRAME.search, { ...FRAME.searchResults, h: 52 }), 6)),
    },
    {
      id: 'shellIcons', chapter: 'shell', scene: 'dashboard', state: dash({ unseen: 2 }),
      cursor: [at(0.1, FRAME.hallBtn), at(0.27, FRAME.homeBtn), at(0.45, FRAME.bell), at(0.65, FRAME.lang), at(0.85, FRAME.theme)],
      spot: (p) => pick(p, [[0.2, grow(FRAME.hallBtn, 5)], [0.37, grow(FRAME.homeBtn, 5)], [0.56, grow(FRAME.bell, 5)], [0.76, grow(FRAME.lang, 5)], [1, grow(FRAME.theme, 5)]]),
    },
    {
      id: 'shellUser', chapter: 'shell', scene: 'dashboard',
      state: dash((p) => ({ frame: { userMenu: p > 0.25 ? (p > 0.45 && p < 0.7 ? 'profile' : true) : false } })),
      cursor: [at(0.15, FRAME.user), at(0.22, FRAME.user, true), at(0.5, FRAME.userMenuProfile), at(0.75, FRAME.userMenuSignOut)],
      spot: (p) => (p < 0.25 ? grow(FRAME.user, 5) : grow(union(FRAME.user, FRAME.userMenu), 5)),
    },
    { id: 'shellTimeout', chapter: 'shell', scene: 'dashboard', state: dash(), cursor: [point(0.3, 760, 360)], spot: () => null, min: 8000 },

    // ── Ana Sayfa ──
    { id: 'dashHero', chapter: 'dashboard', scene: 'dashboard', state: dash(), cursor: [point(0.4, 760, 170)], spot: () => grow(DASH.hero, 5) },
    { id: 'dashTasks', chapter: 'dashboard', scene: 'dashboard', state: dash({ unseen: 2 }), cursor: [at(0.5, DASH.tasksButton)], spot: () => grow(DASH.tasks, 5) },
    {
      id: 'dashGuide', chapter: 'dashboard', scene: 'dashboard',
      state: dash((p) => ({ highlightChip: pick(p, [[0.4, null], [0.55, 'leaveRequest'], [0.7, 'expenseRequest'], [0.85, 'files'], [1, 'messages']]) })),
      cursor: [at(0.2, DASH.guidePlay), point(0.48, 470, 416), point(0.62, 590, 416), point(0.77, 700, 416), point(0.92, 790, 416)],
      spot: () => grow(DASH.guide, 5),
    },
    {
      id: 'dashLinks', chapter: 'dashboard', scene: 'dashboard',
      state: dash((p) => ({ hoverLink: pick(p, [[0.3, 'calendar'], [0.5, 'projects'], [0.7, 'announcements'], [1, 'manual']]) })),
      cursor: [at(0.15, DASH.link(0)), at(0.4, DASH.link(1)), at(0.6, DASH.link(2)), at(0.82, DASH.link(3))],
      spot: (p) => (p < 0.7 ? grow(DASH.links, 5) : grow(DASH.link(3), 5)),
    },

    // ── Görevlerim ──
    {
      id: 'tasksOpen', chapter: 'tasks', scene: 'dashboard',
      state: dash((p) => ({ unseen: p < 0.4 ? 2 : 0, overlay: p >= 0.4 ? 'tasks' : null, appear: span(p, 0.4, 0.55), tasks: {} })),
      cursor: [at(0.2, DASH.tasksButton), at(0.35, DASH.tasksButton, true), point(0.8, 640, 300)],
      spot: (p) => pick(p, [[0.12, grow(FRAME.bell, 6)], [0.38, grow(DASH.tasks, 5)], [1, null]]),
    },
    {
      id: 'tasksStats', chapter: 'tasks', scene: 'dashboard', state: dash({ overlay: 'tasks', tasks: { seen: true } }),
      cursor: [point(0.15, 380, 160), point(0.4, 900, 160), at(0.7, taskFilter[1]), at(0.85, taskFilter[2])],
      spot: (p) => (p < 0.55 ? grow(TASKS.stats, 6) : grow(TASKS.filter, 6)),
    },
    {
      id: 'tasksCard', chapter: 'tasks', scene: 'dashboard', state: dash({ overlay: 'tasks', tasks: { seen: true } }),
      cursor: [point(0.15, 304, 290), point(0.45, 900, 280), point(0.75, 640, 364)],
      spot: (p) => (p < 0.6 ? grow(TASKS.card(0), 5) : grow({ x: 300, y: 258, w: 680, h: 212 }, 5)),
    },
    {
      id: 'tasksStatus', chapter: 'tasks', scene: 'dashboard',
      state: dash((p) => ({ overlay: 'tasks', tasks: { seen: true, expanded: p > 0.15 ? 0 : null, status: p < 0.55 ? 'in-progress' : 'completed' } })),
      cursor: [at(0.08, TASKS.card(0)), at(0.14, TASKS.card(0), true), at(0.4, TASKS.statusPill(1)), at(0.48, TASKS.statusPill(2)), at(0.55, TASKS.statusPill(2), true)],
      spot: (p) => (p < 0.2 ? grow(TASKS.card(0), 5) : grow({ x: 316, y: 408, w: 528, h: 38 }, 6)),
    },

    // ── Projeler penceresi ──
    {
      id: 'projOpen', chapter: 'projectsDialog', scene: 'dashboard',
      state: dash((p) => ({ hoverLink: p < 0.4 ? 'projects' : null, overlay: p >= 0.4 ? 'projects' : null, appear: span(p, 0.4, 0.55), projects: {} })),
      cursor: [at(0.2, DASH.link(1)), at(0.35, DASH.link(1), true), point(0.8, 640, 300)],
      spot: (p) => (p < 0.38 ? grow(DASH.link(1), 5) : grow(PROJ.table, 4)),
    },
    {
      id: 'projFilter', chapter: 'projectsDialog', scene: 'dashboard',
      state: dash((p) => ({ overlay: 'projects', projects: { searchFocus: between(p, 0.1, 0.4) } })),
      cursor: [at(0.12, PROJ.search), at(0.5, PROJ.filters), at(0.82, PROJ.toggle)],
      spot: (p) => pick(p, [[0.4, grow(PROJ.search, 5)], [0.7, grow(PROJ.filters, 5)], [1, grow(PROJ.toggle, 5)]]),
    },
    {
      id: 'projDetail', chapter: 'projectsDialog', scene: 'dashboard',
      state: dash((p) => ({ overlay: p >= 0.3 ? 'projectDetail' : 'projects', detailAppear: span(p, 0.3, 0.45), projects: { detailPressed: between(p, 0.2, 0.3) } })),
      cursor: [at(0.12, PROJ.detail(0)), at(0.22, PROJ.detail(0), true), point(0.6, 640, 380), point(0.85, 640, 520)],
      spot: (p) => pick(p, [[0.3, grow(PROJ.detail(0), 5)], [0.65, grow(PROJ_DETAIL.centers, 4)], [1, grow(PROJ_DETAIL.summary, 4)]]),
    },
    {
      id: 'projReport', chapter: 'projectsDialog', scene: 'dashboard',
      state: dash((p) => ({ overlay: 'projects', projects: { exportPressed: between(p, 0.4, 0.5) } })),
      cursor: [at(0.3, PROJ.export), at(0.42, PROJ.export, true)],
      spot: () => grow(PROJ.export, 6),
    },

    // ── Hatırlatmalar ──
    {
      id: 'remLogin', chapter: 'reminders', scene: 'dashboard', state: dash((p) => ({ overlay: 'reminder', appear: span(p, 0, 0.12), reminder: {} })),
      cursor: [point(0.2, 640, 250), point(0.6, 640, 440)],
      spot: (p) => (p < 0.5 ? grow(REMINDER.sops, 4) : grow(REMINDER.docs, 4)),
    },
    {
      id: 'remButtons', chapter: 'reminders', scene: 'dashboard', state: dash((p) => ({ overlay: 'reminder', reminder: { press: between(p, 0.3, 0.38) ? 'docs' : null } })),
      cursor: [at(0.2, REMINDER.sopAction), at(0.3, REMINDER.docAction, true), at(0.75, REMINDER.later)],
      spot: (p) => pick(p, [[0.25, grow(REMINDER.sopAction, 5)], [0.55, grow(REMINDER.docAction, 5)], [1, grow(REMINDER.later, 5)]]),
    },
    {
      id: 'remBanner', chapter: 'reminders', scene: 'dashboard', state: dash({ deadline: true }),
      cursor: [point(0.15, 640, 106), at(0.55, DEADLINE.complete)],
      spot: () => grow(DEADLINE.banner, 5),
    },
    {
      id: 'remMessage', chapter: 'reminders', scene: 'dashboard', state: dash((p) => ({ message: p > 0.1 })),
      cursor: [point(0.1, 760, 420), at(0.55, MESSAGE_TOAST)],
      spot: (p) => (p > 0.15 ? grow(MESSAGE_TOAST, 5) : null),
    },

    // ── Profil kartı ──
    {
      id: 'pcGo', chapter: 'profileCard', scene: 'dashboard', state: dash((p) => ({ frame: { hover: between(p, 0.2, 0.55) ? 'profile' : null } })),
      cursor: [at(0.25, FRAME.navProfile), at(0.4, FRAME.navProfile, true), at(0.8, FRAME.user)],
      spot: (p) => (p < 0.6 ? grow(FRAME.navProfile, 5) : grow(FRAME.user, 5)),
    },
    {
      id: 'pcOpen', chapter: 'profileCard', scene: 'profile', state: prof('takvim', { scroll: 0, hero: fresh }),
      cursor: [point(0.4, 600, 140)],
      spot: () => grow({ x: 272, y: 88, w: 560, h: 88 }, 6),
    },
    {
      id: 'pcPhoto', chapter: 'profileCard', scene: 'profile',
      state: prof('takvim', (p) => ({
        scroll: 0,
        hero: { ...fresh, photo: p > 0.92, cameraPressed: between(p, 0.15, 0.22) },
        overlay: between(p, 0.24, 0.92) ? 'photo' : null,
        appear: span(p, 0.24, 0.36),
        dialog: { saving: between(p, 0.74, 0.92) },
      })),
      cursor: [at(0.1, H0.camera), at(0.17, H0.camera, true), point(0.45, 640, 280), at(0.62, PHOTO_DLG.save), at(0.72, PHOTO_DLG.save, true)],
      spot: (p) => pick(p, [[0.24, grow(H0.camera, 8)], [0.6, grow(PHOTO_DLG.previews, 4)], [0.92, grow(PHOTO_DLG.save, 5)], [1, grow(H0.avatar, 6)]]),
      toast: (p) => (p > 0.92 ? { text: t('profilePage.hero.photoSaved') } : null),
    },
    {
      id: 'pcStats', chapter: 'profileCard', scene: 'profile',
      state: prof('takvim', (p) => ({ scroll: 0, hero: { ...fresh, photo: true, highlightStat: pick(p, [[0.35, 'projects'], [0.65, 'sops'], [1, 'messages']]) } })),
      cursor: [at(0.15, H0.stat(0)), at(0.45, H0.stat(1)), at(0.75, H0.stat(2))],
      spot: () => grow(H0.stats, 6),
    },
    {
      id: 'pcContacts', chapter: 'profileCard', scene: 'profile',
      state: prof('takvim', (p) => ({
        scroll: 0,
        hero: { ...fresh, photo: true, emergency: p > 0.88 ? '0533 765 43 21' : null, flash: p > 0.88 ? 'emergency' : null },
        overlay: between(p, 0.34, 0.88) ? 'phone' : null,
        appear: span(p, 0.34, 0.44),
        dialog: { value: typed('0533 765 43 21', p, 0.5, 0.68), focused: between(p, 0.46, 0.75) },
      })),
      cursor: [point(0.05, 640, 300), at(0.24, H0.contactEdit(CONTACT_EMERGENCY)), at(0.31, H0.contactEdit(CONTACT_EMERGENCY), true), at(0.46, PHONE_DLG.field, true), at(0.78, PHONE_DLG.save), at(0.84, PHONE_DLG.save, true)],
      spot: (p) => pick(p, [[0.22, grow(H0.contacts, 4)], [0.34, grow(H0.contactEdit(CONTACT_EMERGENCY), 5)], [0.76, grow(PHONE_DLG.field, 6)], [0.88, grow(PHONE_DLG.save, 5)], [1, grow(H0.contact(CONTACT_EMERGENCY), 5)]]),
      toast: (p) => (p > 0.88 ? { text: t('profilePage.dialogs.phone.saved') } : null),
    },
    {
      id: 'pcAddress', chapter: 'profileCard', scene: 'profile',
      state: prof('takvim', (p) => ({
        scroll: 0,
        hero: { ...fresh, photo: true, emergency: '0533 765 43 21', home: p > 0.88 ? t('userGuide.stage.homeAddress') : null, flash: p > 0.88 ? 'home' : null },
        overlay: between(p, 0.2, 0.88) ? 'address' : null,
        appear: span(p, 0.2, 0.3),
        dialog: { home: typed(t('userGuide.stage.homeAddress'), p, 0.34, 0.56), focus: between(p, 0.32, 0.6) ? 'home' : null, primary: 'home' },
      })),
      cursor: [at(0.1, H0.contactEdit(CONTACT_HOME)), at(0.17, H0.contactEdit(CONTACT_HOME), true), at(0.32, ADDRESS_DLG.home, true), at(0.66, ADDRESS_DLG.primaryHome), at(0.78, ADDRESS_DLG.save), at(0.84, ADDRESS_DLG.save, true)],
      spot: (p) => pick(p, [[0.2, grow(H0.contactEdit(CONTACT_HOME), 5)], [0.6, grow(ADDRESS_DLG.home, 6)], [0.76, grow(union(ADDRESS_DLG.primaryHome, ADDRESS_DLG.primaryWork), 6)], [0.88, grow(ADDRESS_DLG.save, 5)], [1, grow(H0.contact(CONTACT_HOME), 5)]]),
      toast: (p) => (p > 0.88 ? { text: t('profilePage.dialogs.address.saved') } : null),
    },
    {
      id: 'pcPassword', chapter: 'profileCard', scene: 'profile',
      state: prof('takvim', (p) => ({
        scroll: 0,
        hero: { ...heroDone, passwordPressed: between(p, 0.12, 0.2) },
        overlay: between(p, 0.2, 0.9) ? 'password' : null,
        appear: span(p, 0.2, 0.3),
        dialog: { filled: pick(p, [[0.32, 0], [0.4, 0.5], [0.46, 1], [0.54, 1.5], [0.6, 2], [0.68, 2.5], [1, 3]]) },
      })),
      cursor: [at(0.08, H0.password), at(0.14, H0.password, true), at(0.34, PASSWORD_DLG.current, true), at(0.48, PASSWORD_DLG.next, true), at(0.62, PASSWORD_DLG.confirm, true), at(0.8, PASSWORD_DLG.submit), at(0.86, PASSWORD_DLG.submit, true)],
      spot: (p) => pick(p, [[0.2, grow(H0.password, 5)], [0.7, grow(union(PASSWORD_DLG.current, PASSWORD_DLG.confirm), 6)], [0.78, grow(PASSWORD_DLG.rules, 4)], [0.9, grow(PASSWORD_DLG.submit, 5)], [1, null]]),
      toast: (p) => (p > 0.9 ? { text: t('profilePage.dialogs.password.saved') } : null),
    },

    // ── Profil tamamlama ──
    {
      id: 'compRing', chapter: 'completion', scene: 'profile',
      state: prof('takvim', (p) => ({ scroll: 0, hero: { ...fresh, completionOpen: p > 0.3 }, overlay: p > 0.3 ? 'completion' : null })),
      cursor: [at(0.15, H0.completion), at(0.28, H0.completion, true), point(0.7, 1048, 300)],
      spot: (p) => (p < 0.3 ? grow(H0.completion, 5) : grow(P0.panel, 4)),
    },
    {
      id: 'compActions', chapter: 'completion', scene: 'profile',
      state: prof('takvim', (p) => {
        const focus = pick(p, [[0.3, 'photo'], [0.5, 'emergencyPhone'], [0.7, 'address'], [1, 'education']]);
        return { scroll: 0, hero: { ...fresh, completionOpen: true, suggestionFocus: focus }, overlay: 'completion' };
      }),
      cursor: [at(0.15, suggestionRect(72, fresh, 'photo')), at(0.4, suggestionRect(72, fresh, 'emergencyPhone')), at(0.6, suggestionRect(72, fresh, 'address')), at(0.8, suggestionRect(72, fresh, 'education'))],
      spot: () => grow({ x: P0.panel.x + 10, y: P0.panel.y + 214, w: P0.panel.w - 20, h: 160 }, 2),
    },

    // ── Sekmeler ──
    {
      id: 'tabsGroups', chapter: 'tabs', scene: 'profile',
      state: prof('takvim', (p) => ({
        scroll: span(p, 0, 0.25),
        navHover: pick(p, [[0.35, null], [0.45, 'dosyalar'], [0.55, 'projelerim'], [0.65, 'izin-yonetimi'], [0.75, 'masraf-yonetimi'], [0.85, 'iletisim-bildirim'], [1, 'cv-yonetimi']]),
      })),
      cursor: [point(0.1, 760, 300), at(0.4, T.takvim.pills.dosyalar), at(0.5, T.takvim.pills.projelerim), at(0.6, T.takvim.pills['izin-yonetimi']), at(0.7, T.takvim.pills['masraf-yonetimi']), at(0.8, T.takvim.pills['iletisim-bildirim']), at(0.92, T.takvim.pills['cv-yonetimi'])],
      spot: (p) => (p < 0.28 ? null : grow({ x: 262, y: NAV_STICKY + 4, w: 976, h: 46 }, 4)),
    },
    {
      id: 'tabsSub', chapter: 'tabs', scene: 'profile',
      state: prof('takvim', (p) => ({ tab: pick(p, [[0.3, 'takvim'], [0.65, 'izin-talep'], [1, 'izin-taleplerim']]) })),
      cursor: [at(0.18, T.takvim.pills['izin-yonetimi']), at(0.28, T.takvim.pills['izin-yonetimi'], true), at(0.55, T.izinTalep.tabs['izin-taleplerim']), at(0.63, T.izinTalep.tabs['izin-taleplerim'], true)],
      spot: (p) => (p < 0.3 ? grow(T.takvim.pills['izin-yonetimi'], 5) : subRow(T.izinTalep)),
    },
    {
      id: 'tabsBadges', chapter: 'tabs', scene: 'profile', state: prof('izin-taleplerim'),
      cursor: [at(0.25, T.izinTaleplerim.pills.dosyalar), at(0.6, T.izinTaleplerim.pills['iletisim-bildirim'])],
      spot: (p) => (p < 0.45 ? grow(T.izinTaleplerim.pills.dosyalar, 5) : grow(T.izinTaleplerim.pills['iletisim-bildirim'], 5)),
    },

    // ── Takvim ──
    {
      id: 'calOverview', chapter: 'calendar', scene: 'profile',
      state: prof('takvim', (p) => ({ pane: { selected: p > 0.42 ? { row: 2, col: 4 } : { row: 1, col: 3 } } })),
      cursor: [point(0.15, 600, 420), at(0.34, C.cell(2, 4)), at(0.4, C.cell(2, 4), true), point(0.8, 1100, 400)],
      spot: (p) => (p < 0.42 ? grow(C.grid, 4) : grow(C.panel, 4)),
    },
    {
      id: 'calAdd', chapter: 'calendar', scene: 'profile', state: prof('takvim'),
      cursor: [at(0.2, C.addVisit), at(0.45, C.addJob), at(0.75, C.excel)],
      spot: (p) => (p < 0.6 ? grow(union(C.addJob, C.addVisit), 5) : grow(C.excel, 5)),
    },
    {
      id: 'calVideo', chapter: 'calendar', scene: 'profile', state: prof('takvim'),
      cursor: [at(0.2, C.help), at(0.5, C.video)],
      spot: (p) => (p < 0.35 ? grow(C.help, 5) : grow(C.video, 6)),
    },

    // ── Ajandam ──
    {
      id: 'agOpen', chapter: 'agenda', scene: 'profile', state: prof('takvim', (p) => ({ tab: p < 0.25 ? 'takvim' : 'ajandam' })),
      cursor: [at(0.12, T.takvim.tabs.ajandam), at(0.22, T.takvim.tabs.ajandam, true), point(0.7, 1110, 360)],
      spot: (p) => (p < 0.25 ? grow(T.takvim.tabs.ajandam, 5) : grow(A.side, 4)),
    },
    {
      id: 'agFill', chapter: 'agenda', scene: 'profile',
      state: prof('ajandam', (p) => ({ pane: { p, filled: p > 0.68 ? 3 : p > 0.4 ? 2 : p > 0.06 ? 1 : 0, typeFrom: 0.08, typeTo: 0.3, focus: pick(p, [[0.06, null], [0.36, 'title'], [0.66, 'description'], [0.84, 'start'], [1, 'end']]) } })),
      cursor: [at(0.04, A.title, true), at(0.38, A.description, true), at(0.68, A.start, true), at(0.84, A.end, true)],
      spot: (p) => pick(p, [[0.36, grow(A.title, 6)], [0.66, grow(A.description, 6)], [1, grow(union(A.start, A.end), 6)]]),
    },
    {
      id: 'agSend', chapter: 'agenda', scene: 'profile',
      state: prof('ajandam', (p) => ({ pane: { filled: 3, pressed: between(p, 0.2, 0.3), opened: p > 0.32, cleared: p > 0.32 } })),
      cursor: [at(0.12, A.submit), at(0.22, A.submit, true), point(0.7, 600, 640)],
      spot: (p) => (p < 0.32 ? grow(A.submit, 5) : grow(A.opened, 4)),
    },

    // ── Dosyalar ──
    {
      id: 'filesCards', chapter: 'files', scene: 'profile', state: prof('dosyalar', (p) => ({ tab: p < 0.22 ? 'ajandam' : 'dosyalar', pane: { category: 'sop' } })),
      cursor: [at(0.1, T.ajandam.pills.dosyalar), at(0.2, T.ajandam.pills.dosyalar, true), at(0.45, F.card(0)), at(0.6, F.card(1)), at(0.75, F.card(2)), at(0.88, F.card(4))],
      spot: (p) => (p < 0.22 ? grow(T.ajandam.pills.dosyalar, 5) : grow(F.cards, 6)),
    },
    {
      id: 'filesOpen', chapter: 'files', scene: 'profile',
      state: prof('dosyalar', (p) => ({ pane: { category: between(p, 0.3, 0.68) ? 'forms' : 'sop', formsSeen: p > 0.3, pressedCard: between(p, 0.24, 0.3) ? 'forms' : between(p, 0.62, 0.68) ? 'sop' : null } })),
      cursor: [at(0.18, F.card(2)), at(0.26, F.card(2), true), point(0.45, 640, F.row(0).y + 28), at(0.58, F.card(0)), at(0.65, F.card(0), true)],
      spot: (p) => pick(p, [[0.3, grow(F.card(2), 5)], [0.58, grow({ x: 256, y: F.row(0).y, w: 1000, h: 118 }, 4)], [1, grow(F.row(0), 4)]]),
    },
    {
      id: 'filesFilter', chapter: 'files', scene: 'profile',
      state: prof('dosyalar', (p) => ({ pane: { category: 'sop', formsSeen: true, filter: pick(p, [[0.3, 'all'], [0.62, 'unread'], [1, 'all']]), searchFocus: between(p, 0.72, 0.98) } })),
      cursor: [at(0.22, sopFilter[1]), at(0.28, sopFilter[1], true), at(0.56, sopFilter[0]), at(0.6, sopFilter[0], true), at(0.72, F.search, true)],
      spot: (p) => (p < 0.68 ? grow(F.filter, 5) : grow(F.search, 5)),
    },
    {
      id: 'filesForms', chapter: 'files', scene: 'profile',
      state: prof('dosyalar', (p) => ({ pane: { category: p < 0.5 ? 'forms' : 'sop', formsSeen: true } })),
      cursor: [at(0.2, F.row(0)), at(0.6, F.card(3)), at(0.8, F.card(4))],
      spot: (p) => pick(p, [[0.5, grow(F.row(0), 4)], [1, grow({ x: F.card(3).x, y: F.card(3).y, w: 394, h: 124 }, 5)]]),
    },

    // ── SOP okuma ──
    {
      id: 'sopOpen', chapter: 'sop', scene: 'profile',
      state: prof('dosyalar', (p) => ({ pane: { category: 'sop', formsSeen: true, focusRow: p < 0.3 ? 0 : null }, overlay: p > 0.3 ? 'sop' : null, appear: span(p, 0.3, 0.42), dialog: { unlocked: false } })),
      cursor: [at(0.15, F.row(0)), at(0.24, F.row(0), true), point(0.7, 640, 300)],
      spot: (p) => (p < 0.3 ? grow(F.row(0), 4) : grow(SOP_DLG.viewer, 4)),
    },
    {
      id: 'sopLock', chapter: 'sop', scene: 'profile', state: prof('dosyalar', (p) => ({ pane: { category: 'sop' }, overlay: 'sop', dialog: { unlocked: p > 0.5 } })),
      cursor: [point(0.3, 600, 514)],
      spot: () => grow(union(SOP_DLG.confirm, SOP_DLG.hint), 5),
    },
    {
      id: 'sopConfirm', chapter: 'sop', scene: 'profile',
      state: prof('dosyalar', (p) => ({ pane: { category: 'sop' }, overlay: p > 0.38 ? 'quiz' : 'sop', appear: span(p, 0.38, 0.5), dialog: { unlocked: true, checked: p > 0.24, question: 1 } })),
      cursor: [at(0.12, SOP_DLG.checkbox), at(0.22, SOP_DLG.checkbox, true), point(0.8, 640, 360)],
      spot: (p) => (p < 0.38 ? grow(SOP_DLG.confirm, 5) : null),
    },
    {
      id: 'sopQuiz', chapter: 'sop', scene: 'profile',
      state: prof('dosyalar', (p) => ({
        pane: { category: 'sop' },
        overlay: 'quiz',
        dialog: { unlocked: true, checked: true, question: p < 0.52 ? 1 : 5, answer: p < 0.52 ? (p > 0.3 ? 1 : -1) : (p > 0.75 ? 0 : -1) },
      })),
      cursor: [at(0.22, QUIZ_DLG.choice(1)), at(0.3, QUIZ_DLG.choice(1), true), at(0.44, QUIZ_DLG.next), at(0.5, QUIZ_DLG.next, true), at(0.68, QUIZ_DLG.choice(0)), at(0.75, QUIZ_DLG.choice(0), true), at(0.88, QUIZ_DLG.next)],
      spot: (p) => (p > 0.84 ? grow(QUIZ_DLG.next, 5) : null),
    },
    {
      id: 'sopDone', chapter: 'sop', scene: 'profile',
      state: prof('dosyalar', (p) => ({ pane: { category: 'sop', sopRead: p > 0.45 }, overlay: p < 0.45 ? 'quiz' : null, dialog: { unlocked: true, checked: true, result: true } })),
      cursor: [at(0.2, QUIZ_DLG.next), at(0.35, QUIZ_DLG.next, true), point(0.75, 1100, F.row(0).y + 28)],
      spot: (p) => (p < 0.45 ? grow(QUIZ_DLG.next, 5) : grow(union(F.card(0), F.row(0)), 4)),
      toast: (p) => (p > 0.45 ? { text: t('profilePage.files.toast.markedRead') } : null),
    },

    // ── Eğitimler ──
    {
      id: 'trList', chapter: 'training', scene: 'profile', state: prof('dosyalar', (p) => ({ pane: { category: p < 0.15 ? 'sop' : 'education', sopRead: true, pressedCard: between(p, 0.08, 0.15) ? 'education' : null } })),
      cursor: [at(0.05, F.card(1)), at(0.12, F.card(1), true), at(0.4, F.row(0)), at(0.65, F.row(1)), at(0.85, F.row(3))],
      spot: (p) => pick(p, [[0.15, grow(F.card(1), 5)], [0.55, grow(F.row(0), 4)], [0.75, grow(F.row(1), 4)], [1, grow(F.row(3), 4)]]),
    },
    {
      id: 'trPlayer', chapter: 'training', scene: 'profile',
      state: prof('dosyalar', (p) => ({ pane: { category: 'education' }, overlay: p > 0.25 ? 'training' : null, appear: span(p, 0.25, 0.37), dialog: { progress: 0.62 } })),
      cursor: [at(0.1, F.row(0)), at(0.2, F.row(0), true), point(0.5, 560, 526), point(0.8, 1040, 260)],
      spot: (p) => pick(p, [[0.25, grow(F.row(0), 4)], [0.62, grow(TRAIN_DLG.timeline, 10)], [1, grow(TRAIN_DLG.chapters, 4)]]),
    },
    {
      id: 'trPop', chapter: 'training', scene: 'profile',
      state: prof('dosyalar', (p) => ({ pane: { category: 'education' }, overlay: 'training', dialog: { progress: 0.7, pop: p < 0.9, popAnswer: p > 0.5 ? 1 : -1 } })),
      cursor: [point(0.2, 640, 260), at(0.42, TRAIN_DLG.popChoice(1)), at(0.5, TRAIN_DLG.popChoice(1), true), at(0.72, TRAIN_DLG.popSubmit), at(0.8, TRAIN_DLG.popSubmit, true)],
      spot: (p) => (p < 0.7 ? grow(TRAIN_DLG.pop, 3) : grow(TRAIN_DLG.popSubmit, 5)),
    },
    {
      id: 'trExam', chapter: 'training', scene: 'profile', state: prof('dosyalar', { pane: { category: 'education' }, overlay: 'training', dialog: { progress: 0.96, examReady: true } }),
      cursor: [point(0.15, 1040, 470), at(0.5, TRAIN_DLG.startExam)],
      spot: (p) => (p < 0.4 ? grow(TRAIN_DLG.final, 4) : grow(TRAIN_DLG.startExam, 5)),
    },

    // ── Projelerim ──
    {
      id: 'mpCards', chapter: 'myProjects', scene: 'profile', state: prof('projelerim', (p) => ({ tab: p < 0.2 ? 'dosyalar' : 'projelerim', pane: { category: 'education' } })),
      cursor: [at(0.08, T.dosyalar.pills.projelerim), at(0.18, T.dosyalar.pills.projelerim, true), point(0.45, 420, M.card(0).y + 108), point(0.65, 760, M.card(1).y + 108), at(0.86, M.toggle)],
      spot: (p) => pick(p, [[0.2, grow(T.dosyalar.pills.projelerim, 5)], [0.75, grow({ x: 256, y: M.card(0).y, w: 1000, h: 132 }, 5)], [1, grow(M.toggle, 5)]]),
    },
    {
      id: 'mpVisits', chapter: 'myProjects', scene: 'profile',
      state: prof('projelerim', (p) => ({ pane: { pressed: between(p, 0.16, 0.24) ? 0 : null }, overlay: p > 0.24 ? 'visits' : null, appear: span(p, 0.24, 0.36), dialog: { dateFocus: pick(p, [[0.45, null], [0.6, 'start'], [0.72, 'end'], [1, null]]), filtered: p > 0.72 } })),
      cursor: [at(0.1, M.card(0)), at(0.18, M.card(0), true), at(0.48, VISITS.start, true), at(0.62, VISITS.end, true), point(0.85, 640, 380)],
      spot: (p) => pick(p, [[0.24, grow(M.card(0), 5)], [0.76, grow(union(VISITS.start, VISITS.end), 6)], [1, grow(VISITS.table, 4)]]),
    },
    {
      id: 'mpDetail', chapter: 'myProjects', scene: 'profile',
      state: prof('projelerim', (p) => ({ overlay: 'visits', dialog: { filtered: true, detailPressed: between(p, 0.28, 0.36), excelPressed: between(p, 0.74, 0.82) } })),
      cursor: [at(0.2, VISITS.detail(0)), at(0.3, VISITS.detail(0), true), at(0.42, VISITS.detail(2)), at(0.66, VISITS.excel), at(0.76, VISITS.excel, true)],
      spot: (p) => pick(p, [[0.38, grow(VISITS.detail(0), 5)], [0.55, grow(VISITS.row(2), 3)], [1, grow(VISITS.excel, 5)]]),
    },

    // ── İzin talep ──
    {
      id: 'lrType', chapter: 'leaveRequest', scene: 'profile',
      state: prof('izin-talep', (p) => ({ tab: p < 0.2 ? 'projelerim' : 'izin-talep', pane: { type: between(p, 0.6, 0.82) ? 'mazeret' : 'yillik' } })),
      cursor: [at(0.08, T.projelerim.pills['izin-yonetimi']), at(0.17, T.projelerim.pills['izin-yonetimi'], true), at(0.52, L.type(1)), at(0.58, L.type(1), true), at(0.76, L.type(0)), at(0.82, L.type(0), true)],
      spot: (p) => (p < 0.2 ? grow(T.projelerim.pills['izin-yonetimi'], 5) : grow(L.types, 6)),
    },
    {
      id: 'lrBalance', chapter: 'leaveRequest', scene: 'profile', state: prof('izin-talep'),
      cursor: [at(0.15, L.balanceTile(0)), at(0.4, L.balanceTile(1)), at(0.6, L.balanceTile(2)), at(0.82, L.balanceTile(3))],
      spot: () => grow(L.balance, 6),
    },
    {
      id: 'lrDates', chapter: 'leaveRequest', scene: 'profile',
      state: prof('izin-talep', (p) => ({ pane: { filled: p > 0.5 ? 1 : 0, focus: pick(p, [[0.15, null], [0.38, 'start'], [0.5, 'end'], [1, null]]) } })),
      cursor: [at(0.12, L.start, true), at(0.4, L.end, true), point(0.75, 515, L.days.y + 21)],
      spot: (p) => (p < 0.5 ? grow(union(L.start, L.end), 6) : grow({ ...L.days, h: 62 }, 6)),
    },
    {
      id: 'lrMore', chapter: 'leaveRequest', scene: 'profile',
      state: prof('izin-talep', (p) => ({
        paneScroll: span(p, 0, 0.2) * 150,
        pane: { filled: p > 0.6 ? 3 : p > 0.32 ? 2 : 1, reportChosen: p > 0.88, focus: pick(p, [[0.22, null], [0.34, 'travel'], [0.5, null], [0.6, 'reason'], [0.72, 'address'], [1, null]]) },
      })),
      cursor: [at(0.22, LS.travel, true), at(0.5, LS.reason, true), at(0.62, LS.address, true), at(0.8, LS.report), at(0.86, LS.report, true)],
      spot: (p) => pick(p, [[0.2, null], [0.42, grow(LS.travel, 6)], [0.76, grow(union(LS.reason, LS.address), 6)], [1, grow({ ...LS.report, w: 320 }, 6)]]),
    },
    {
      id: 'lrSend', chapter: 'leaveRequest', scene: 'profile',
      state: prof('izin-talep', (p) => ({
        paneScroll: 150,
        pane: p < 0.45 ? { filled: 3, reportChosen: true, pressed: between(p, 0.24, 0.3), sending: between(p, 0.3, 0.45) } : { filled: 0, submitted: true },
      })),
      cursor: [at(0.15, LS.submit), at(0.24, LS.submit, true), point(0.8, 760, LS.balance.y + 26)],
      spot: (p) => (p < 0.45 ? grow(LS.submit, 6) : grow(LS.balance, 6)),
      toast: (p) => (p > 0.45 ? { text: t('profilePage.leave.form.sent') } : null),
    },

    // ── İzin taleplerim ──
    {
      id: 'llList', chapter: 'leaveList', scene: 'profile', state: prof('izin-taleplerim', (p) => ({ tab: p < 0.22 ? 'izin-talep' : 'izin-taleplerim' })),
      cursor: [at(0.1, T.izinTalep.tabs['izin-taleplerim']), at(0.2, T.izinTalep.tabs['izin-taleplerim'], true), point(0.6, 760, LL.card(0).y + 60)],
      spot: (p) => (p < 0.22 ? grow(T.izinTalep.tabs['izin-taleplerim'], 5) : grow(union(LL.card(0), LL.card(1)), 4)),
    },
    {
      id: 'llFilter', chapter: 'leaveList', scene: 'profile', state: prof('izin-taleplerim', (p) => ({ pane: { filter: pick(p, [[0.3, 'ALL'], [0.65, 'APPROVED'], [1, 'ALL']]) } })),
      cursor: [at(0.2, leaveFilter[2]), at(0.28, leaveFilter[2], true), at(0.55, leaveFilter[0]), at(0.63, leaveFilter[0], true)],
      spot: () => grow(LL.filter, 6),
    },
    {
      id: 'llNote', chapter: 'leaveList', scene: 'profile', state: prof('izin-taleplerim', (p) => ({ pane: { highlight: p < 0.5 ? 0 : 1 } })),
      cursor: [point(0.2, 1150, LL.card(0).y + 30), point(0.6, 640, LL.note(1).y + 25)],
      spot: (p) => (p < 0.5 ? grow(LL.card(0), 4) : grow(LL.note(1), 6)),
    },

    // ── Masraf talep ──
    {
      id: 'erForm', chapter: 'expenseRequest', scene: 'profile',
      state: prof('masraf-talep', (p) => ({ tab: p < 0.2 ? 'izin-taleplerim' : 'masraf-talep', pane: { filled: p > 0.75 ? 2 : p > 0.42 ? 1 : 0, focus: pick(p, [[0.28, null], [0.42, 'amount'], [0.58, 'category'], [0.75, 'description'], [1, null]]) } })),
      cursor: [at(0.08, T.izinTaleplerim.pills['masraf-yonetimi']), at(0.17, T.izinTaleplerim.pills['masraf-yonetimi'], true), at(0.3, E.amount, true), at(0.46, E.category, true), at(0.62, E.description, true)],
      spot: (p) => pick(p, [[0.2, grow(T.izinTaleplerim.pills['masraf-yonetimi'], 5)], [0.58, grow(union(E.amount, E.category), 6)], [1, grow(E.description, 6)]]),
    },
    {
      id: 'erDocs', chapter: 'expenseRequest', scene: 'profile', state: prof('masraf-talep', (p) => ({ pane: { filled: 2, focus: between(p, 0.12, 0.45) ? 'drop' : null, files: p > 0.45 ? 2 : 0 } })),
      cursor: [at(0.2, E.drop), at(0.3, E.drop, true), point(0.75, 520, E.files.y + 18)],
      spot: (p) => (p < 0.45 ? grow(E.drop, 6) : grow(E.files, 6)),
    },
    {
      id: 'erSend', chapter: 'expenseRequest', scene: 'profile',
      state: prof('masraf-talep', (p) => ({ pane: p < 0.42 ? { filled: 2, files: 2, pressed: between(p, 0.2, 0.28), sending: between(p, 0.28, 0.42) } : { success: true } })),
      cursor: [at(0.12, E.submit), at(0.2, E.submit, true), at(0.7, E.goTracking)],
      spot: (p) => pick(p, [[0.42, grow(E.submit, 6)], [0.65, grow(E.success, 4)], [1, grow(E.goTracking, 5)]]),
    },

    // ── Masraf takip ──
    {
      id: 'etList', chapter: 'expenseTrack', scene: 'profile', state: prof('masraf-takip', (p) => ({ tab: p < 0.2 ? 'masraf-talep' : 'masraf-takip' })),
      cursor: [at(0.08, T.masrafTalep.tabs['masraf-takip']), at(0.17, T.masrafTalep.tabs['masraf-takip'], true), point(0.45, 760, ET.stats.y + 30), point(0.75, 760, ET.card(0).y + 60)],
      spot: (p) => pick(p, [[0.2, grow(T.masrafTalep.tabs['masraf-takip'], 5)], [0.55, grow(ET.stats, 5)], [1, grow(union(ET.filter, ET.card(1)), 5)]]),
    },
    {
      id: 'etEdit', chapter: 'expenseTrack', scene: 'profile', state: prof('masraf-takip', (p) => ({ pane: { editPressed: between(p, 0.18, 0.26) }, overlay: p > 0.26 ? 'expenseEdit' : null, appear: span(p, 0.26, 0.38) })),
      cursor: [at(0.1, ET.edit(0)), at(0.18, ET.edit(0), true), point(0.55, 640, 380), at(0.82, EXPENSE_EDIT.replace)],
      spot: (p) => pick(p, [[0.26, grow(ET.edit(0), 5)], [0.7, grow(EXPENSE_EDIT.existing, 5)], [1, grow(EXPENSE_EDIT.replace, 5)]]),
    },
    {
      id: 'etRejected', chapter: 'expenseTrack', scene: 'profile', state: prof('masraf-takip', (p) => ({ pane: { filter: p < 0.3 ? 'all' : 'rejected' } })),
      cursor: [at(0.18, expenseFilter[3]), at(0.26, expenseFilter[3], true), point(0.7, 500, ET.card(0).y + 120)],
      spot: (p) => (p < 0.3 ? grow(ET.filter, 5) : grow(ET.card(0), 5)),
    },

    // ── Mesajlar ──
    {
      id: 'msgOpen', chapter: 'messages', scene: 'profile',
      state: prof('mesajlar', (p) => ({ tab: p < 0.2 ? 'masraf-takip' : 'mesajlar', pane: { opened: p > 0.58, pressed: between(p, 0.5, 0.58) } })),
      cursor: [at(0.08, T.masrafTakip.pills['iletisim-bildirim']), at(0.17, T.masrafTakip.pills['iletisim-bildirim'], true), at(0.42, MS.open), at(0.52, MS.open, true)],
      spot: (p) => pick(p, [[0.2, grow(T.masrafTakip.pills['iletisim-bildirim'], 5)], [0.58, grow(MS.open, 6)], [1, null]]),
    },
    {
      id: 'msgPick', chapter: 'messages', scene: 'profile',
      state: prof('mesajlar', (p) => ({ pane: { opened: true, searchFocus: between(p, 0.1, 0.42), search: typed('Meh', p, 0.15, 0.3), chat: p > 0.52 } })),
      cursor: [at(0.08, MS.search, true), at(0.42, MS.person(0)), at(0.5, MS.person(0), true), point(0.85, 900, 320)],
      spot: (p) => pick(p, [[0.38, grow(MS.search, 6)], [0.52, grow(MS.person(0), 4)], [1, null]]),
    },
    {
      id: 'msgSend', chapter: 'messages', scene: 'profile',
      state: prof('mesajlar', (p) => ({ pane: { opened: true, chat: true, inputFocus: p < 0.6, typedText: true, typeFrom: 0.1, typeTo: 0.42, sent: p > 0.58, sendPressed: between(p, 0.5, 0.58) } })),
      cursor: [at(0.05, MS.input, true), at(0.46, MS.send), at(0.54, MS.send, true), at(0.82, MS.emoji)],
      spot: (p) => pick(p, [[0.46, grow(MS.input, 6)], [0.6, grow(MS.send, 6)], [1, grow(MS.emoji, 6)]]),
    },
    {
      id: 'msgFile', chapter: 'messages', scene: 'profile',
      state: prof('mesajlar', (p) => ({ pane: { opened: true, chat: true, sent: true, attachFocus: between(p, 0.1, 0.3), file: p > 0.3, fileSent: p > 0.68 } })),
      cursor: [at(0.12, MS.attach), at(0.22, MS.attach, true), at(0.58, MS.send), at(0.66, MS.send, true), point(0.85, 1100, T.mesajlar.top + 250)],
      spot: (p) => pick(p, [[0.3, grow(MS.attach, 6)], [0.68, grow(MS.send, 6)], [1, grow({ x: 1080, y: T.mesajlar.top + 210, w: 160, h: 90 }, 4)]]),
    },
    {
      id: 'msgCall', chapter: 'messages', scene: 'profile', state: prof('mesajlar', { pane: { opened: true, chat: true, sent: true, fileSent: true } }),
      cursor: [at(0.3, MS.voice), at(0.6, MS.video)],
      spot: () => grow(union(MS.voice, MS.video), 6),
    },

    // ── Bildirimler ──
    {
      id: 'annList', chapter: 'announcements', scene: 'profile', state: prof('bildirimler', (p) => ({ tab: p < 0.25 ? 'mesajlar' : 'bildirimler', pane: { opened: true, chat: true } })),
      cursor: [at(0.1, T.mesajlar.tabs.bildirimler), at(0.2, T.mesajlar.tabs.bildirimler, true), point(0.6, 640, AN.item(0).y + 40)],
      spot: (p) => (p < 0.25 ? grow(T.mesajlar.tabs.bildirimler, 5) : grow(union(AN.item(0), AN.item(1)), 5)),
    },

    // ── Hata bildir ──
    {
      id: 'bugForm', chapter: 'bugReport', scene: 'profile',
      state: prof('sistem-hata-bildirimleri', (p) => ({ tab: p < 0.22 ? 'bildirimler' : 'sistem-hata-bildirimleri', pane: { typed: true, typeFrom: 0.32, typeTo: 0.85, focus: p > 0.28 ? 'summary' : null } })),
      cursor: [at(0.08, T.bildirimler.tabs['sistem-hata-bildirimleri']), at(0.18, T.bildirimler.tabs['sistem-hata-bildirimleri'], true), at(0.28, B.summary, true)],
      spot: (p) => pick(p, [[0.22, grow(T.bildirimler.tabs['sistem-hata-bildirimleri'], 5)], [0.3, grow(B.tips, 4)], [1, grow(B.summary, 6)]]),
    },
    {
      id: 'bugShot', chapter: 'bugReport', scene: 'profile',
      state: prof('sistem-hata-bildirimleri', (p) => ({ pane: p < 0.8 ? { done: true, shotPressed: between(p, 0.16, 0.24), shot: p > 0.3, pressed: between(p, 0.7, 0.8) } : {} })),
      cursor: [at(0.1, B.screenshot), at(0.18, B.screenshot, true), at(0.6, B.submit), at(0.72, B.submit, true)],
      spot: (p) => pick(p, [[0.5, grow({ ...B.screenshot, w: 360 }, 6)], [0.8, grow(B.submit, 6)], [1, null]]),
      toast: (p) => (p > 0.8 ? { text: t('profilePage.errorReport.sent') } : null),
    },

    // ── CV / GCP / IATA ──
    {
      id: 'cvTabs', chapter: 'cv', scene: 'profile', state: prof('cv-tr', (p) => ({ tab: p < 0.2 ? 'sistem-hata-bildirimleri' : 'cv-tr' })),
      cursor: [at(0.08, T.hata.pills['cv-yonetimi']), at(0.17, T.hata.pills['cv-yonetimi'], true), at(0.4, T.cvTr.tabs['cv-tr']), at(0.55, T.cvTr.tabs['cv-en']), at(0.7, T.cvTr.tabs.gcp), at(0.85, T.cvTr.tabs.iata)],
      spot: (p) => (p < 0.2 ? grow(T.hata.pills['cv-yonetimi'], 5) : subRow(T.cvTr)),
    },
    {
      id: 'cvTemplate', chapter: 'cv', scene: 'profile', state: prof('cv-tr', (p) => ({ pane: { templateFocus: p > 0.2 } })),
      cursor: [at(0.4, CVR.template)],
      spot: () => grow(CVR.template, 6),
    },
    {
      id: 'cvUpload', chapter: 'cv', scene: 'profile',
      state: prof('cv-tr', (p) => ({ pane: { dropFocus: between(p, 0.12, 0.32), chosen: p > 0.3 && p < 0.75, pressed: between(p, 0.52, 0.6), uploading: between(p, 0.6, 0.75), uploaded: p >= 0.75, flash: p >= 0.75 } })),
      cursor: [at(0.15, CVR.drop), at(0.24, CVR.drop, true), at(0.46, CVR.submit), at(0.55, CVR.submit, true), point(0.85, 500, CVR.row.y + 30)],
      spot: (p) => pick(p, [[0.42, grow(CVR.drop, 6)], [0.75, grow(CVR.submit, 6)], [1, grow(CVR.list, 4)]]),
      toast: (p) => (p >= 0.75 ? { text: t('profilePage.cv.toast.uploaded', { count: 1, type: 'CV-TR' }) } : null),
    },
    {
      id: 'cvReplace', chapter: 'cv', scene: 'profile', state: prof('cv-tr', (p) => ({ pane: { uploaded: true, hoverAction: pick(p, [[0.25, 'view'], [0.45, 'download'], [0.65, 'delete'], [1, null]]) } })),
      cursor: [at(0.1, CVR.view), at(0.32, CVR.download), at(0.52, CVR.delete), point(0.85, 1000, CVR.drop.y + 50)],
      spot: (p) => (p < 0.65 ? grow(CVR.actions, 5) : grow(CVR.drop, 5)),
    },

    { id: 'end', chapter: 'end', scene: 'title', state: () => ({ variant: 'end' }), cursor: null, min: 11000 },
  ];
};

/** Beats with start/duration for the current language, the chapters and the total length. */
export const buildTimeline = (t) => {
  let start = 0;
  const beats = BEATS(t).map((beat, index) => {
    const caption = t(`userGuide.beats.${beat.id}`);
    const duration = Math.max(beat.min || 0, caption.length * 66 + 1900);
    const entry = { ...beat, index, caption, start, duration };
    start += duration;
    return entry;
  });
  const chapters = CHAPTER_KEYS.map((chapter) => {
    const inChapter = beats.filter((beat) => beat.chapter === chapter);
    const first = inChapter[0];
    const last = inChapter[inChapter.length - 1];
    return { key: chapter, start: first.start, end: last.start + last.duration, beats: inChapter };
  });
  return { beats, chapters, total: start };
};

